import { Link, useLocation } from 'react-router-dom';
import { Home, Heart, PlusCircle, Baby, User } from 'lucide-react';
import { useFrontendAuth } from '../hooks/useAuth';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

/** Guest links go straight to login (with return path) so mobile never flashes a protected shell. */
function authAwarePath(user: unknown, target: string): string {
    if (user) return target;
    return `/login?returnUrl=${encodeURIComponent(target)}`;
}

const MobileBottomNav = () => {
    const location = useLocation();
    const { user } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    const items = [
        {
            to: '/home',
            label: tr('nav.home', 'Home'),
            icon: Home,
            match: (p: string) => p === '/' || p === '/home' || p.startsWith('/search') || p.startsWith('/product'),
            tourId: undefined as string | undefined,
        },
        {
            to: authAwarePath(user, '/favourites'),
            label: tr('nav.favourites', 'Saved'),
            icon: Heart,
            match: (p: string) => p.startsWith('/favourites'),
            tourId: undefined as string | undefined,
        },
        {
            to: authAwarePath(user, '/sell'),
            label: tr('header.sell', 'Sell'),
            icon: PlusCircle,
            match: (p: string) => p.startsWith('/sell') || p.startsWith('/edit-product'),
            tourId: 'home.sell.mobile',
        },
        {
            to: authAwarePath(user, '/my-petito'),
            label: tr('nav.journey', 'Journey'),
            icon: Baby,
            match: (p: string) => p.startsWith('/my-petito') || p.startsWith('/my-rebaby'),
            tourId: 'home.journey.mobile',
        },
        {
            to: user ? '/my-account' : '/login',
            label: tr('nav.account', 'Account'),
            icon: User,
            match: (p: string) =>
                p.startsWith('/my-account') ||
                p.startsWith('/login') ||
                p.startsWith('/register') ||
                p.startsWith('/users/'),
            tourId: undefined as string | undefined,
        },
    ];

    return (
        <nav
            data-mobile-bottom-nav
            // Only env(safe-area) — avoid max(0.75rem) which adds fake clearance on non-notch devices
            // and double-counts with page pb-mobile-nav on iPhone.
            className="fixed bottom-0 left-0 right-0 z-50 w-full max-w-[100vw] overflow-x-hidden border-t border-[var(--accent-color)]/50 bg-[var(--bg-color)]/95 backdrop-blur-md pb-[env(safe-area-inset-bottom,0px)] lg:hidden"
            aria-label={tr('nav.mobile', 'Mobile navigation')}
        >
            <ul className="mx-auto flex h-16 w-full max-w-lg items-stretch justify-between px-0.5 box-border">
                {items.map(({ to, label, icon: Icon, match, tourId }) => {
                    const active = match(location.pathname);
                    return (
                        <li key={to} className="flex min-w-0 flex-1 basis-0">
                            <Link
                                to={to}
                                data-tour-id={tourId}
                                aria-current={active ? 'page' : undefined}
                                className={`mx-0.5 flex min-h-[44px] w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl px-0.5 text-[10px] font-medium transition-colors ${
                                    active
                                        ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_35%)] text-[var(--text-color)] shadow-sm'
                                        : 'text-[color-mix(in_srgb,var(--text-color),transparent_45%)]'
                                }`}
                            >
                                <Icon
                                    className={`h-5 w-5 shrink-0 ${active ? 'stroke-[2.25]' : ''}`}
                                    aria-hidden
                                />
                                <span className="max-w-full truncate px-0.5">{label}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default MobileBottomNav;
